// GS Mandal — Public Tender listing + detail pages.
// Only tenders whose status = "Published" are returned by the backend.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { FileText, Calendar, Clock, ArrowRight, ArrowLeft, Building2, MapPin, Filter, Download, AlertCircle } from "lucide-react";

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };
const fmtDateTime = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
const daysUntil = (s) => { if (!s) return null; try { const d = Math.ceil((new Date(s) - new Date()) / (1000 * 60 * 60 * 24)); return d; } catch { return null; } };

function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#0D5C63] via-[#0D5C63] to-[#083E43] text-white">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 90% 10%, #F26419 0, transparent 45%)" }} />
      <div className="relative max-w-6xl mx-auto px-5 py-14">
        <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Procurement</div>
        <h1 className="trust-serif text-4xl sm:text-5xl mt-2 leading-tight">Tenders & <span className="text-[#F26419]">Notices</span></h1>
        <p className="mt-4 max-w-3xl text-white/85 leading-relaxed">Active tenders, expressions of interest and RFPs issued by G.S. Mandal and its member institutes. Please read every notice carefully — corrigenda are appended below each tender.</p>
      </div>
    </section>
  );
}

export default function TrustTendersList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/public/tenders");
        setList(data || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((t) => [t.title, t.reference_number, t.tender_type, t.category, t.subcategory, t.institute_name, t.short_description].join(" ").toLowerCase().includes(s));
  }, [list, q]);

  return (
    <div data-testid="trust-tenders-page">
      <Hero />

      <section className="max-w-6xl mx-auto px-5 py-12">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5B5B]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, reference, category…"
              className="w-full bg-white border border-[#E9E2D2] rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]"
              data-testid="trust-tenders-search"
            />
          </div>
          <div className="text-[10px] text-[#5B5B5B]">{filtered.length} active</div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="text-sm text-[#5B5B5B]">Loading tenders…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-[#E9E2D2] rounded-2xl p-12 text-center">
              <FileText size={28} strokeWidth={1.5} className="mx-auto text-[#0D5C63]" />
              <div className="trust-serif text-lg text-[#1A1A1A] mt-3">No active tenders at the moment</div>
              <p className="text-sm text-[#5B5B5B] mt-1">Please check back later — new tenders are published as they arise.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((t) => <TenderCard key={t.id} t={t} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function TenderCard({ t }) {
  const d = daysUntil(t.last_submission_datetime);
  const urgent = d != null && d >= 0 && d <= 7;
  const overdue = d != null && d < 0;
  return (
    <Link to={t.id} className="bg-white border border-[#E9E2D2] rounded-2xl p-5 hover:shadow-lg transition-shadow group block" data-testid={`trust-tender-card-${t.id}`}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#0D5C63] font-bold">
        <FileText size={12} /> {t.tender_type || "Tender"}
        {t.category && <><span className="text-[#E9E2D2]">·</span><span>{t.category}</span></>}
        {((t.subcategories && t.subcategories.length ? t.subcategories : (t.subcategory ? [t.subcategory] : []))).map((s, i) => <React.Fragment key={i}><span className="text-[#E9E2D2]">·</span><span>{s}</span></React.Fragment>)}
      </div>
      <div className="trust-serif text-xl text-[#1A1A1A] leading-snug mt-2 group-hover:text-[#F26419] transition-colors">{t.title}</div>
      {t.reference_number && <div className="text-[11px] text-[#5B5B5B] mt-1">Ref: <span className="font-mono text-[#1A1A1A]">{t.reference_number}</span></div>}
      {t.short_description && <div className="text-sm text-[#3E3E3E] mt-3 leading-relaxed line-clamp-3">{t.short_description}</div>}
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-[#5B5B5B]">
        <span className="inline-flex items-center gap-1"><Building2 size={11} /> {t.entity_scope === "Institute" ? (t.institute_name || "Institute") : "GS Mandal"}</span>
        {t.publication_date && <span className="inline-flex items-center gap-1"><Calendar size={11} /> Pub: {fmtDate(t.publication_date)}</span>}
      </div>
      {t.last_submission_datetime && (
        <div className={`mt-3 rounded-md px-3 py-2 text-[11px] font-semibold flex items-center gap-2 ${overdue ? "bg-red-50 text-red-800" : urgent ? "bg-orange-50 text-orange-800" : "bg-[#F5EFE3] text-[#0D5C63]"}`}>
          <Clock size={12} /> Last date: {fmtDateTime(t.last_submission_datetime)}
          {d != null && !overdue && <span className="ml-auto">{d} day{d === 1 ? "" : "s"} left</span>}
          {overdue && <span className="ml-auto">Closed</span>}
        </div>
      )}
      <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#F26419] group-hover:text-[#D90368]">
        View details <ArrowRight size={12} />
      </div>
    </Link>
  );
}

export function TrustTenderDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/public/tenders/${id}`);
        setT(data);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="max-w-4xl mx-auto px-5 py-16 text-sm text-[#5B5B5B]">Loading tender…</div>;
  if (notFound || !t) return (
    <div className="max-w-4xl mx-auto px-5 py-16 text-center" data-testid="trust-tender-notfound">
      <AlertCircle size={32} className="mx-auto text-[#D90368]" />
      <div className="trust-serif text-2xl mt-3">Tender not available</div>
      <p className="text-sm text-[#5B5B5B] mt-1">This tender may have been closed, cancelled or archived.</p>
      <Link to=".." relative="path" className="mt-6 inline-flex items-center gap-1 text-[#0D5C63] hover:text-[#F26419] text-xs font-semibold uppercase tracking-wider">
        <ArrowLeft size={13} /> Back to all tenders
      </Link>
    </div>
  );

  const rfe = (t.attachments || []).find((a) => a.kind === "rfe");
  const supporting = (t.attachments || []).filter((a) => a.kind !== "rfe");

  return (
    <div data-testid="trust-tender-detail">
      <Hero />
      <section className="max-w-4xl mx-auto px-5 py-12">
        <Link to=".." relative="path" className="text-xs font-semibold uppercase tracking-wider text-[#0D5C63] hover:text-[#F26419] inline-flex items-center gap-1" data-testid="trust-tender-back">
          <ArrowLeft size={13} /> All Tenders
        </Link>

        <div className="mt-4 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#0D5C63] font-bold">
          <FileText size={12} /> {t.tender_type}
          {t.category && <span>· {t.category}</span>}
          {((t.subcategories && t.subcategories.length ? t.subcategories : (t.subcategory ? [t.subcategory] : []))).map((s, i) => <span key={i}>· {s}</span>)}
        </div>
        <h1 className="trust-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-2 leading-tight">{t.title}</h1>
        {t.reference_number && <div className="text-sm text-[#5B5B5B] mt-1">Ref No.: <span className="font-mono text-[#1A1A1A]">{t.reference_number}</span></div>}

        <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-[#5B5B5B]">
          <span className="inline-flex items-center gap-1"><Building2 size={11} /> {t.entity_scope === "Institute" ? (t.institute_name || "Institute") : "GS Mandal Trust"}</span>
        </div>

        {t.short_description && <p className="mt-5 text-[15px] text-[#3E3E3E] leading-relaxed">{t.short_description}</p>}

        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          <DateRow label="Publication Date" v={fmtDate(t.publication_date)} />
          <DateRow label="Bid Opening Date" v={fmtDate(t.bid_opening_date)} />
          <DateRow label="Last Submission" v={fmtDateTime(t.last_submission_datetime)} highlight />
          <DateRow label="Pre-Bid Meeting" v={fmtDate(t.pre_bid_meeting_date)} />
          <DateRow label="Clarification Deadline" v={fmtDate(t.clarification_deadline)} />
          <DateRow label="Technical Bid Opening" v={fmtDate(t.technical_bid_opening_date)} />
          <DateRow label="Financial Bid Opening" v={fmtDate(t.financial_bid_opening_date)} />
          <DateRow label="Tender Validity" v={fmtDate(t.validity_date)} />
        </div>

        {t.long_description && (
          <Section title="Detailed Description">
            <div className="whitespace-pre-wrap text-[14px] text-[#1A1A1A] leading-relaxed">{t.long_description}</div>
          </Section>
        )}
        {t.eligibility_criteria && (
          <Section title="Eligibility Criteria">
            <div className="whitespace-pre-wrap text-[14px] text-[#1A1A1A] leading-relaxed">{t.eligibility_criteria}</div>
          </Section>
        )}
        {t.submission_mode === "Offline" && (
          <Section title="Submission Mode">
            <div className="bg-[#0F0F10] text-white rounded-lg px-4 py-3 flex items-start gap-3 border-l-4 border-[#c78e42]" data-testid="tender-offline-notice">
              <div className="text-[#c78e42] font-bold mt-0.5">■</div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#c78e42] font-semibold">Offline / Physical Submission</div>
                <div className="text-sm mt-1">Bids must be submitted in hard copy at the GS Mandal office. Online portal upload is not enabled for this tender.</div>
              </div>
            </div>
          </Section>
        )}
        {t.submission_details && (
          <Section title="Submission Details">
            <div className="whitespace-pre-wrap text-[14px] text-[#1A1A1A] leading-relaxed">{t.submission_details}</div>
          </Section>
        )}

        {(rfe || supporting.length > 0) && (
          <Section title="Documents">
            <div className="space-y-2">
              {rfe && <AttachRow a={rfe} accent />}
              {supporting.map((a, i) => <AttachRow key={i} a={a} />)}
            </div>
          </Section>
        )}

        {(t.corrigenda || []).length > 0 && (
          <Section title="Corrigenda">
            <ul className="space-y-3">
              {t.corrigenda.map((c, i) => (
                <li key={c.id || i} className="border-l-2 border-[#F26419] bg-[#FAFAF7] pl-4 py-2 rounded-r-md">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm font-semibold text-[#1A1A1A]">{c.title}</div>
                    <div className="text-[10px] text-[#5B5B5B]">{fmtDate(c.corr_date)}</div>
                  </div>
                  {c.note && <div className="text-[13px] text-[#3E3E3E] mt-1 whitespace-pre-wrap">{c.note}</div>}
                  {c.attachment_url && (
                    <a href={c.attachment_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#0D5C63] hover:text-[#F26419]"><Download size={11} /> Corrigendum PDF</a>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </section>
    </div>
  );
}

function DateRow({ label, v, highlight }) {
  return (
    <div className={`border rounded-lg px-3 py-2 ${highlight ? "bg-[#F26419]/8 border-[#F26419]/30" : "bg-white border-[#E9E2D2]"}`}>
      <div className="text-[9px] tracking-[0.25em] uppercase text-[#5B5B5B] font-semibold">{label}</div>
      <div className={`text-sm mt-0.5 ${highlight ? "text-[#D90368] font-bold" : "text-[#1A1A1A]"}`}>{v}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold">{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function AttachRow({ a, accent }) {
  return (
    <a href={a.url} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-between gap-3 rounded-lg px-4 py-3 border transition-colors ${accent ? "bg-[#0D5C63] hover:bg-[#083E43] text-white border-[#0D5C63]" : "bg-white hover:bg-[#F5EFE3] text-[#1A1A1A] border-[#E9E2D2]"}`}>
      <div className="flex items-center gap-2 min-w-0">
        <FileText size={14} />
        <div className="truncate text-sm">{accent ? "Tender Document (RFE)" : (a.label || "Supporting Document")}</div>
      </div>
      <Download size={13} className="shrink-0" />
    </a>
  );
}
